import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { customerIdZodSchema } from "@@/shared/users/customer-id";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { PayRequestId } from "../pay-request-id";
import { payRequestPaymentIdZodSchema } from "../pay-request-payment-id";
import { payRequestPaymentStripeDataZodObject } from "./_pay-request-payment-stripe-data";
import { payRequestPaymentSwishDataZodObject } from "./_pay-request-payment-swish-data";
import type { PayRequestEvent } from "./_pay-request.event";
import { payRequestEventIdFactory } from "./_pay-request.event-id";
import { payRequestEventSchemaFactory } from "./_pay-request.event.schema-factory";

const _payRequestPaymentInitiatedEventZodObject = z.object({
    initiatedAt: isoAndUnixZodSchema,
    initiatedBy: userIdZodSchema.or(customerIdZodSchema).nullable(),
    // .or(z.literal("ANONYMOUS")),
    amount: z.number().int().positive(),
    payRequestPaymentId: payRequestPaymentIdZodSchema,
    type: z.literal("PAY_REQUEST_PAYMENT_INITIATED_EVENT"),
});

const _stripeDataZodObject = _payRequestPaymentInitiatedEventZodObject.merge(
    payRequestPaymentStripeDataZodObject,
);
const _swishDataZodObject = _payRequestPaymentInitiatedEventZodObject.merge(
    payRequestPaymentSwishDataZodObject,
);

const payRequestPaymentInitiatedEventStripeZodObject =
    payRequestEventSchemaFactory(
        "PAY_REQUEST_PAYMENT_INITIATED_EVENT",
        _stripeDataZodObject,
    );
const payRequestPaymentInitiatedEventSwishZodObject =
    payRequestEventSchemaFactory(
        "PAY_REQUEST_PAYMENT_INITIATED_EVENT",
        _swishDataZodObject,
    );

const payRequestPaymentInitiatedData = z.discriminatedUnion("paymentProvider", [
    _stripeDataZodObject,
    _swishDataZodObject,
]);

type PayRequestPaymentInitiatedEventData = z.infer<
    typeof payRequestPaymentInitiatedData
>;

const payRequestPaymentInitiatedEventZodObject = payRequestEventSchemaFactory(
    "PAY_REQUEST_PAYMENT_INITIATED_EVENT",
    payRequestPaymentInitiatedData,
);
type PayRequestPaymentInitiatedEvent = z.infer<
    typeof payRequestPaymentInitiatedEventZodObject
>;

type PayRequestPaymentInitiatedEventFactoryParams = {
    payRequestId: PayRequestId;
    happenedAt: IsoAndUnixTimestamp;
    version: number;
    paymentFor: PayRequestEvent["paymentFor"];
    data: Except<PayRequestPaymentInitiatedEvent["data"], "type">;
};
const payRequestPaymentInitiatedEventFactory = (
    params: PayRequestPaymentInitiatedEventFactoryParams,
): PayRequestPaymentInitiatedEvent => {
    return {
        _id: payRequestEventIdFactory(),
        _type: "PAY_REQUEST_EVENT",
        _createdAt: nowIsoAndUnix(),
        _happenedAt: params.happenedAt,
        _version: params.version,
        type: "PAY_REQUEST_PAYMENT_INITIATED_EVENT",
        data: {
            ...params.data,
            type: "PAY_REQUEST_PAYMENT_INITIATED_EVENT",
            initiatedBy: params.data.initiatedBy,
        },
        payRequestId: params.payRequestId,
        paymentFor: params.paymentFor,
    };
};

const isPayRequestPaymentInitiatedEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is PayRequestPaymentInitiatedEvent => {
    return (
        event?._type === "PAY_REQUEST_EVENT" &&
        event?.type === "PAY_REQUEST_PAYMENT_INITIATED_EVENT"
    );
};
const isPayRequestPaymentInitiatedEvent_Stripe = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is PayRequestPaymentInitiatedEvent & {
    data: z.infer<typeof _stripeDataZodObject>;
} => {
    return (
        event?._type === "PAY_REQUEST_EVENT" &&
        event?.type === "PAY_REQUEST_PAYMENT_INITIATED_EVENT" &&
        event?.data?.paymentProvider === "STRIPE"
    );
};

const isPayRequestPaymentInitiatedEvent_Swish = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is PayRequestPaymentInitiatedEvent & {
    data: z.infer<typeof _swishDataZodObject>;
} => {
    return (
        event?._type === "PAY_REQUEST_EVENT" &&
        event?.type === "PAY_REQUEST_PAYMENT_INITIATED_EVENT" &&
        event?.data?.paymentProvider === "SWISH"
    );
};

export {
    isPayRequestPaymentInitiatedEvent,
    isPayRequestPaymentInitiatedEvent_Stripe,
    isPayRequestPaymentInitiatedEvent_Swish,
    payRequestPaymentInitiatedEventFactory,
    payRequestPaymentInitiatedEventStripeZodObject,
    payRequestPaymentInitiatedEventSwishZodObject,
    payRequestPaymentInitiatedEventZodObject,
};
export type {
    PayRequestPaymentInitiatedEvent,
    PayRequestPaymentInitiatedEventData,
};
